import { useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  ResponsiveContainer,
} from 'recharts';
import { Minus, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CountUp } from './CountUp';

/**
 * fleetGUARDIAN KPI cards. Card chrome is 100% Tailwind (flat white Panel);
 * only KpiLineCard / MultiKpiCard embed a tiny Recharts sparkline.
 *
 * KPI cards must read SERVER-computed aggregates only — never sum rows
 * client-side. These components are presentation-only; the numbers arrive
 * pre-aggregated from the API.
 */

// ---------------------------------------------------------------------------
// Icon-tile accent hues (soft bg-50 tile + text-600 glyph). Full class strings
// are listed so Tailwind's JIT scanner keeps them.
// ---------------------------------------------------------------------------

export type KpiHue =
  | 'navy'
  | 'indigo'
  | 'blue'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'amber'
  | 'orange'
  | 'red'
  | 'rose'
  | 'violet'
  | 'gray';

const HUE_TILE: Record<KpiHue, string> = {
  navy: 'bg-blue-50 text-[#1B4F83] dark:bg-blue-500/10 dark:text-blue-300',
  indigo: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300',
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300',
  emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
  teal: 'bg-teal-50 text-teal-600 dark:bg-teal-500/10 dark:text-teal-300',
  cyan: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-500/10 dark:text-cyan-300',
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300',
  orange: 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300',
  red: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300',
  rose: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300',
  violet: 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300',
  gray: 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300',
};

const PANEL_BASE =
  'rounded-xl border border-gray-200 bg-white transition-shadow hover:shadow-md dark:border-white/10 dark:bg-card';

// ---------------------------------------------------------------------------
// Delta pill (shared by KpiCard + HeroKpi). Polarity-aware via `invertDelta`.
// ---------------------------------------------------------------------------

export interface DeltaPillProps {
  /** Signed percentage delta vs prior period; positive = up. */
  delta: number;
  /** When true an upward delta is bad (e.g. fraud count) → rendered red. */
  invertDelta?: boolean;
}

export function DeltaPill({ delta, invertDelta = false }: DeltaPillProps): JSX.Element {
  const isFlat = delta === 0;
  const isUp = delta > 0;
  const good = invertDelta ? !isUp : isUp;
  const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
        isFlat
          ? 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300'
          : good
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
            : 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
      )}
    >
      <Icon className="h-3 w-3" />
      {Math.abs(delta).toFixed(1)}%
    </span>
  );
}

function IconTile({
  icon: Icon,
  hue = 'navy',
  large,
}: {
  icon?: LucideIcon;
  hue?: KpiHue;
  large?: boolean;
}): JSX.Element | null {
  if (!Icon) return null;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        large ? 'h-12 w-12 rounded-xl' : 'h-10 w-10 rounded-lg',
        HUE_TILE[hue],
      )}
    >
      <Icon className={large ? 'h-6 w-6' : 'h-5 w-5'} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// AutoFitValue — keep a KPI figure on ONE line, shrinking only when needed.
// ---------------------------------------------------------------------------
//
// SA currency grouping makes these strings long: "R 3 164 541 842" and
// "126 818 637 L" both wrapped mid-number at the card width, so one figure read
// as two. Hard-coding a smaller font would shrink every card to suit the worst
// case, so each value is measured instead and stepped down only when it
// actually overflows — "2.3%" keeps the full size.
//
// Floors at 62% of the class-driven size so a pathological value degrades to
// small-but-legible. Re-measures on container resize and on every render, so a
// CountUp animation settles on the final, widest string.
const MIN_SCALE = 0.62;

function AutoFitValue({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.fontSize = '';
    const base = parseFloat(window.getComputedStyle(el).fontSize);
    if (!base) return;
    const floor = base * MIN_SCALE;
    let size = base;
    // +1 tolerates sub-pixel rounding, which would otherwise shrink a value
    // that already fits.
    while (el.scrollWidth > el.clientWidth + 1 && size > floor) {
      size -= 0.5;
      el.style.fontSize = `${size}px`;
    }
  }, []);

  useLayoutEffect(fit);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  return (
    <div ref={ref} className={cn('overflow-hidden whitespace-nowrap', className)}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KpiCard — icon tile + optional delta pill + label + value + subtitle.
// ---------------------------------------------------------------------------

export interface KpiCardProps {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  hue?: KpiHue;
  delta?: number | null;
  invertDelta?: boolean;
  subtitle?: ReactNode;
  className?: string;
}

export function KpiCard({
  label,
  value,
  icon,
  hue = 'navy',
  delta,
  invertDelta,
  subtitle,
  className,
}: KpiCardProps): JSX.Element {
  return (
    <div className={cn(PANEL_BASE, 'flex flex-col gap-3 p-3 sm:p-4', className)}>
      <div className="flex items-start justify-between gap-2">
        <IconTile icon={icon} hue={hue} />
        {delta != null && <DeltaPill delta={delta} invertDelta={invertDelta} />}
      </div>
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-muted-foreground">
          {label}
        </div>
        <AutoFitValue className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-foreground sm:text-2xl">
          {value}
        </AutoFitValue>
        {subtitle && (
          <div className="mt-1 text-xs text-gray-400 dark:text-muted-foreground">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroKpi — bigger entry-point tile with count-up value + optional delta/link.
// ---------------------------------------------------------------------------

export interface HeroKpiProps {
  label: string;
  /** Numeric value (count-up animates from 0 to this). */
  value: number;
  icon?: LucideIcon;
  hue?: KpiHue;
  /** Format the animating value. Default: rounded integer, SA grouping. */
  format?: (v: number) => string;
  hint?: ReactNode;
  delta?: number | null;
  invertDelta?: boolean;
  /** When false the value renders statically (no count-up). Default true. */
  countUp?: boolean;
  /** Optional client route — turns the whole card into a link. */
  to?: string;
  className?: string;
}

export function HeroKpi({
  label,
  value,
  icon,
  hue = 'navy',
  format,
  hint,
  delta,
  invertDelta,
  countUp = true,
  to,
  className,
}: HeroKpiProps): JSX.Element {
  const fmt = format ?? ((v: number) => new Intl.NumberFormat('en-ZA').format(Math.round(v)));
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <IconTile icon={icon} hue={hue} large />
        {delta != null && <DeltaPill delta={delta} invertDelta={invertDelta} />}
      </div>
      <div className="mt-3">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-muted-foreground">
          {label}
        </div>
        <AutoFitValue className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-foreground sm:text-3xl">
          {countUp ? <CountUp value={value} format={fmt} /> : fmt(value)}
        </AutoFitValue>
        {hint && (
          <div className="mt-1 text-xs text-gray-500 dark:text-muted-foreground">
            {hint}
          </div>
        )}
      </div>
    </>
  );

  const base = cn(
    PANEL_BASE,
    'block p-4 motion-safe:hover:-translate-y-0.5 motion-safe:transition-transform sm:p-5',
    className,
  );

  return to ? (
    <Link to={to} className={base}>
      {inner}
    </Link>
  ) : (
    <div className={base}>{inner}</div>
  );
}

// ---------------------------------------------------------------------------
// KpiLineCard — icon tile + weighted-avg annotation + value + 64px sparkline.
// ---------------------------------------------------------------------------

export interface SparkPoint {
  period: string;
  value: number;
}

export interface KpiLineCardProps {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  hue?: KpiHue;
  /** Weighted moving-average annotation, e.g. "Wt. Avg 4.2". */
  weightedAvg?: string;
  trend: SparkPoint[];
  /** Sparkline stroke colour. */
  color?: string;
  className?: string;
}

export function KpiLineCard({
  label,
  value,
  icon,
  hue = 'navy',
  weightedAvg,
  trend,
  color = '#1F6B75',
  className,
}: KpiLineCardProps): JSX.Element {
  return (
    <div className={cn(PANEL_BASE, 'flex flex-col gap-3 p-3 sm:p-4', className)}>
      <div className="flex items-start justify-between gap-2">
        <IconTile icon={icon} hue={hue} />
        {weightedAvg && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-600 dark:bg-white/5 dark:text-gray-300">
            {weightedAvg}
          </span>
        )}
      </div>
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-muted-foreground">
          {label}
        </div>
        <AutoFitValue className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-foreground sm:text-2xl">
          {value}
        </AutoFitValue>
      </div>
      <div className="h-16 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MultiKpiCard — title + total + a grid of sub-values, each with a sparkbar.
// ---------------------------------------------------------------------------

export interface MultiKpiItem {
  label: string;
  value: ReactNode;
  /** Optional mini area sparkbar values. */
  trend?: number[];
  color?: string;
}

export interface MultiKpiCardProps {
  title: string;
  total?: ReactNode;
  icon?: LucideIcon;
  hue?: KpiHue;
  items: MultiKpiItem[];
  className?: string;
}

// Static so Tailwind's JIT scanner keeps the class strings.
const MULTI_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
};

export function MultiKpiCard({
  title,
  total,
  icon,
  hue = 'navy',
  items,
  className,
}: MultiKpiCardProps): JSX.Element {
  const cols = MULTI_COLS[Math.min(items.length, 4)] ?? 'grid-cols-2 sm:grid-cols-4';
  return (
    <div className={cn(PANEL_BASE, 'flex flex-col gap-3 p-3 sm:p-4', className)}>
      <div className="flex items-center gap-3">
        <IconTile icon={icon} hue={hue} />
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-muted-foreground">
            {title}
          </div>
          {total != null && (
            <div className="text-xl font-semibold tabular-nums text-gray-900 dark:text-foreground">
              {total}
            </div>
          )}
        </div>
      </div>
      <div className={cn('grid gap-3', cols)}>
        {items.map((item) => (
          <div key={item.label} className="min-w-0">
            <div className="truncate text-[11px] text-gray-500 dark:text-muted-foreground">
              {item.label}
            </div>
            <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-foreground">
              {item.value}
            </div>
            {item.trend && item.trend.length > 1 && (
              <div className="mt-1 h-6 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={item.trend.map((v, i) => ({ i, v }))}
                    margin={{ top: 2, right: 0, bottom: 0, left: 0 }}
                  >
                    <Area
                      type="monotone"
                      dataKey="v"
                      stroke={item.color ?? '#1F6B75'}
                      fill={item.color ?? '#1F6B75'}
                      fillOpacity={0.15}
                      strokeWidth={1.5}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
